// ============================================================================
// CSC master configs — one CscMasterCfg per simple configuration screen. Wired
// into app.routes.ts as route `data.cfg` so the shared CscMasterComponent renders
// each with zero per-screen boilerplate.
// ============================================================================
import { CscMasterCfg } from './csc-master.component';
import { CscReportCfg } from './csc-report.component';

export const CSC_CATEGORIES: CscMasterCfg = {
  title: 'Ticket Categories', subtitle: 'Classify support tickets by type.', base: '/api/CSC/Categories',
  idKey: 'categoryID', addLabel: '+ Add Category',
  columns: [{ key: 'name', label: 'Category' }, { key: 'description', label: 'Description' }],
  fields: [{ key: 'name', label: 'Name', type: 'text', required: true }, { key: 'description', label: 'Description', type: 'textarea' }]
};

export const CSC_PRIORITIES: CscMasterCfg = {
  title: 'Ticket Priorities', subtitle: 'Priority levels with target SLA hours.', base: '/api/CSC/Priorities',
  idKey: 'priorityID', addLabel: '+ Add Priority',
  columns: [{ key: 'name', label: 'Priority' }, { key: 'slaResponseHours', label: 'Response (h)', align: 'right' },
    { key: 'slaResolutionHours', label: 'Resolution (h)', align: 'right' }, { key: 'colorHex', label: 'Color', type: 'color' },
    { key: 'sortOrder', label: 'Order', align: 'right' }],
  fields: [{ key: 'name', label: 'Name', type: 'text', required: true }, { key: 'slaResponseHours', label: 'SLA Response Hours', type: 'number' },
    { key: 'slaResolutionHours', label: 'SLA Resolution Hours', type: 'number' }, { key: 'colorHex', label: 'Color', type: 'color' },
    { key: 'sortOrder', label: 'Sort Order', type: 'number' }]
};

export const CSC_STATUSES: CscMasterCfg = {
  title: 'Ticket Statuses', subtitle: 'Workflow states. "Closed" statuses stop the SLA clock.', base: '/api/CSC/Statuses',
  idKey: 'statusID', addLabel: '+ Add Status',
  columns: [{ key: 'name', label: 'Status' }, { key: 'isClosed', label: 'Closed', type: 'bool', align: 'center' },
    { key: 'colorHex', label: 'Color', type: 'color' }, { key: 'sortOrder', label: 'Order', align: 'right' }],
  fields: [{ key: 'name', label: 'Name', type: 'text', required: true }, { key: 'isClosed', label: 'Marks the ticket as closed / resolved', type: 'checkbox' },
    { key: 'colorHex', label: 'Color', type: 'color' }, { key: 'sortOrder', label: 'Sort Order', type: 'number' }]
};

export const CSC_AGENTS: CscMasterCfg = {
  title: 'Support Agents', subtitle: 'The team who handle support tickets.', base: '/api/CSC/Agents',
  idKey: 'agentID', addLabel: '+ Add Agent',
  columns: [{ key: 'name', label: 'Agent' }, { key: 'email', label: 'Email' }, { key: 'phone', label: 'Phone' }, { key: 'team', label: 'Team' }],
  fields: [{ key: 'name', label: 'Name', type: 'text', required: true }, { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' }, { key: 'team', label: 'Team', type: 'text' }]
};

export const CSC_SLA: CscMasterCfg = {
  title: 'SLA Policies', subtitle: 'Response & resolution targets, optionally per priority.', base: '/api/CSC/SlaPolicies',
  idKey: 'slaPolicyID', addLabel: '+ Add SLA Policy',
  columns: [{ key: 'name', label: 'Policy' }, { key: 'priorityName', label: 'Priority' },
    { key: 'responseHours', label: 'Response (h)', align: 'right' }, { key: 'resolutionHours', label: 'Resolution (h)', align: 'right' }],
  fields: [{ key: 'name', label: 'Name', type: 'text', required: true }, { key: 'priorityID', label: 'Priority', type: 'select', source: 'priorities' },
    { key: 'responseHours', label: 'Response Hours', type: 'number' }, { key: 'resolutionHours', label: 'Resolution Hours', type: 'number' },
    { key: 'description', label: 'Description', type: 'textarea' }]
};

export const CSC_CANNED: CscMasterCfg = {
  title: 'Canned Responses', subtitle: 'Reusable reply templates for faster support.', base: '/api/CSC/CannedResponses',
  idKey: 'cannedID', addLabel: '+ Add Response',
  columns: [{ key: 'title', label: 'Title' }, { key: 'body', label: 'Body' }],
  fields: [{ key: 'title', label: 'Title', type: 'text', required: true }, { key: 'body', label: 'Body', type: 'textarea' }]
};

export const CSC_KB: CscMasterCfg = {
  title: 'Knowledge Base Articles', subtitle: 'Help articles for customers and agents.', base: '/api/CSC/KbArticles',
  idKey: 'articleID', addLabel: '+ Add Article',
  columns: [{ key: 'title', label: 'Title' }, { key: 'category', label: 'Category' },
    { key: 'isPublished', label: 'Published', type: 'bool', align: 'center' }, { key: 'views', label: 'Views', align: 'right' }],
  fields: [{ key: 'title', label: 'Title', type: 'text', required: true }, { key: 'category', label: 'Category', type: 'text' },
    { key: 'body', label: 'Body', type: 'textarea' }, { key: 'isPublished', label: 'Published (visible to customers)', type: 'checkbox' }]
};

// ---- CSC Reports ----------------------------------------------------------
export const CSC_REP_TICKETS: CscReportCfg = {
  title: 'Tickets Report', subtitle: 'All support tickets in the selected window.', endpoint: '/api/CSC/reports/tickets',
  filters: [
    { key: 'dateFrom', label: 'From', type: 'date' }, { key: 'dateTo', label: 'To', type: 'date' },
    { key: 'statusID', label: 'Status', type: 'select', source: 'statuses' },
    { key: 'priorityID', label: 'Priority', type: 'select', source: 'priorities' },
    { key: 'agentID', label: 'Agent', type: 'select', source: 'agents' },
    { key: 'categoryID', label: 'Category', type: 'select', source: 'categories' }
  ],
  columns: [
    { key: 'ticketNumber', label: 'Ticket #' }, { key: 'subject', label: 'Subject' }, { key: 'customer', label: 'Customer' },
    { key: 'category', label: 'Category' }, { key: 'priority', label: 'Priority' }, { key: 'status', label: 'Status' },
    { key: 'agent', label: 'Agent' }, { key: 'channel', label: 'Channel' }, { key: 'opened', label: 'Opened' }, { key: 'resolved', label: 'Resolved' }
  ]
};

export const CSC_REP_AGENT: CscReportCfg = {
  title: 'Agent Performance Report', subtitle: 'Tickets handled and average resolution time per agent.', endpoint: '/api/CSC/reports/agent-performance',
  filters: [{ key: 'dateFrom', label: 'From', type: 'date' }, { key: 'dateTo', label: 'To', type: 'date' }],
  columns: [
    { key: 'agent', label: 'Agent' }, { key: 'team', label: 'Team' }, { key: 'assigned', label: 'Assigned', align: 'right' },
    { key: 'open', label: 'Open', align: 'right' }, { key: 'resolved', label: 'Resolved', align: 'right' },
    { key: 'avgResolutionHours', label: 'Avg Resolution (h)', align: 'right' }
  ]
};

export const CSC_REP_SLA: CscReportCfg = {
  title: 'SLA Compliance Report', subtitle: 'Resolved tickets: on-time vs breached against their due date.', endpoint: '/api/CSC/reports/sla-compliance',
  filters: [{ key: 'dateFrom', label: 'From', type: 'date' }, { key: 'dateTo', label: 'To', type: 'date' }, { key: 'priorityID', label: 'Priority', type: 'select', source: 'priorities' }],
  columns: [
    { key: 'ticketNumber', label: 'Ticket #' }, { key: 'subject', label: 'Subject' }, { key: 'priority', label: 'Priority' },
    { key: 'due', label: 'Due' }, { key: 'resolved', label: 'Resolved' }, { key: 'resolutionHours', label: 'Resolution (h)', align: 'right' }, { key: 'sla', label: 'SLA' }
  ]
};
